import type {
  Biome,
  Companion,
  GameState,
  Inventory,
  LogEntry,
  Pace,
  Rations,
  TimeOfDay,
  TrailEvent,
  Weather,
} from "./types";
import {
  DIFFICULTIES,
  LANDMARKS,
  TOTAL_DISTANCE,
  TRAIL_EVENTS,
  randomAilment,
} from "./data";
import type { Difficulty } from "./types";

const PACE_MILES: Record<Pace, number> = {
  steady: 40,
  strenuous: 60,
  grueling: 80,
};

const PACE_HEALTH_COST: Record<Pace, number> = {
  steady: 2,
  strenuous: 6,
  grueling: 12,
};

const RATIONS_FOOD_COST: Record<Rations, number> = {
  filling: 6,
  meager: 4,
  "bare-bones": 2,
};

const RATIONS_HEALTH: Record<Rations, number> = {
  filling: 3, // heals
  meager: 0,
  "bare-bones": -4, // starves
};

// simple deterministic-ish RNG helper seeded per action
export function makeRng(seed: number): () => number {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

let logCounter = 0;
export function makeLog(
  day: number,
  message: string,
  tone: LogEntry["tone"],
): LogEntry {
  logCounter += 1;
  return { id: `${Date.now()}-${logCounter}`, day, message, tone };
}

export function createInitialState(): GameState {
  return {
    phase: "menu",
    day: 1,
    distance: 0,
    totalDistance: TOTAL_DISTANCE,
    currentLandmarkIndex: 0,
    difficulty: "pleb",
    pace: "steady",
    rations: "filling",
    party: [],
    inventory: { sats: 0, food: 0, medicine: 0, gear: 0 },
    log: [],
    activeEventId: null,
    weather: "clear",
    seed: Math.floor(Math.random() * 2147483000) + 1,
  };
}

export const DEFAULT_PARTY_NAMES = ["You", "Ada", "Hal", "Nick"];

export function startGame(names: string[], difficulty: Difficulty = "pleb"): GameState {
  const party: Companion[] = names
    .filter((n) => n.trim().length > 0)
    .map((n) => ({
      name: n.trim(),
      health: 100,
      alive: true,
      ailment: null,
    }));

  const base = createInitialState();
  const cfg = DIFFICULTIES[difficulty];
  return {
    ...base,
    phase: "outfitting",
    difficulty,
    party: party.length ? party : [{ name: "You", health: 100, alive: true, ailment: null }],
    inventory: { ...cfg.startingInventory },
    log: [makeLog(1, "Your party sets out from Genesis Station.", "info")],
  };
}

function clampInventory(inv: Inventory): Inventory {
  return {
    sats: Math.max(0, Math.round(inv.sats)),
    food: Math.max(0, Math.round(inv.food)),
    medicine: Math.max(0, Math.round(inv.medicine)),
    gear: Math.max(0, Math.round(inv.gear)),
  };
}

function applyPartyDamage(party: Companion[], dmg: number, rng: () => number): Companion[] {
  const living = party.filter((p) => p.alive);
  if (!living.length) return party;
  return party.map((p) => {
    if (!p.alive) return p;
    // spread damage with a little randomness; negative dmg heals
    const jitter = 0.6 + rng() * 0.8;
    const delta = dmg * jitter;
    const health = Math.max(0, Math.min(100, p.health - delta));
    return {
      ...p,
      health,
      alive: health > 0,
    };
  });
}

// Time of day advances one phase per travel step: dawn → day → dusk → night.
// This makes the sky visibly shift on every "Continue" click.
export function timeOfDayForDay(day: number): TimeOfDay {
  const phases: TimeOfDay[] = ["day", "dusk", "night", "dawn"];
  return phases[(day - 1) % phases.length];
}

function rollWeather(rng: () => number, biome: Biome): Weather {
  const r = rng();
  if (biome === "mountains") {
    if (r < 0.35) return "snow";
    if (r < 0.5) return "rain";
    return "clear";
  }
  if (biome === "river") {
    if (r < 0.3) return "rain";
    return "clear";
  }
  if (r < 0.15) return "rain";
  return "clear";
}

export function beginOutfitting(state: GameState): GameState {
  return { ...state, phase: "traveling" };
}

// Advance one travel step. Either triggers an event, reaches a landmark, or reports safe travel.
export function advance(state: GameState): GameState {
  const rng = makeRng(state.seed + state.day * 7919);
  const newSeed = Math.floor(rng() * 2147483000) + 1;

  const cfg = DIFFICULTIES[state.difficulty];

  const day = state.day + 1;
  const miles = PACE_MILES[state.pace];
  const distance = Math.min(state.totalDistance, state.distance + miles);

  // consume food (scaled by difficulty)
  const foodEaten =
    RATIONS_FOOD_COST[state.rations] *
    Math.max(1, state.party.filter((p) => p.alive).length) *
    cfg.foodCostMult /
    2;
  const inv = clampInventory({ ...state.inventory, food: state.inventory.food - foodEaten });

  const log: LogEntry[] = [];

  // health effects from pace and rations (scaled by difficulty)
  let party = state.party;
  const outOfFood = inv.food <= 0;
  const healthDelta =
    (PACE_HEALTH_COST[state.pace] - RATIONS_HEALTH[state.rations] + (outOfFood ? 10 : 0)) *
    cfg.damageMult;
  party = applyPartyDamage(party, healthDelta, rng);

  if (outOfFood) {
    log.push(makeLog(day, "You're out of food! The party is starving.", "bad"));
  }

  // random ailment chance (scaled by difficulty)
  if (rng() < cfg.ailmentChance) {
    const candidates = party.filter((p) => p.alive && !p.ailment);
    if (candidates.length) {
      const victim = candidates[Math.floor(rng() * candidates.length)];
      const ailment = randomAilment(rng);
      party = party.map((p) =>
        p.name === victim.name ? { ...p, ailment } : p,
      );
      log.push(makeLog(day, `${victim.name} came down with ${ailment}.`, "bad"));
    }
  }

  // note deaths
  state.party.forEach((prev) => {
    const now = party.find((p) => p.name === prev.name);
    if (prev.alive && now && !now.alive) {
      log.push(makeLog(day, `${prev.name} did not survive the trail. 🪦`, "bad"));
    }
  });

  // roll the day's weather (biome-aware)
  const upcomingBiome =
    LANDMARKS[Math.min(state.currentLandmarkIndex + 1, LANDMARKS.length - 1)].biome;
  const weather = rollWeather(rng, upcomingBiome);
  if (weather === "rain") {
    log.push(makeLog(day, "Rain drums on the wagon canvas.", "info"));
  } else if (weather === "snow") {
    log.push(makeLog(day, "Snow blankets Difficulty Ridge. The going is slow.", "info"));
  }

  const base: GameState = {
    ...state,
    day,
    distance,
    inventory: inv,
    party,
    weather,
    seed: newSeed,
    log: [...log, ...state.log].slice(0, 40),
  };

  // check game over — everyone dead
  if (base.party.every((p) => !p.alive)) {
    return { ...base, phase: "gameover" };
  }

  // reached the end?
  if (distance >= base.totalDistance) {
    return {
      ...base,
      currentLandmarkIndex: LANDMARKS.length - 1,
      phase: "victory",
    };
  }

  // reached a new landmark?
  const nextLandmark = LANDMARKS[base.currentLandmarkIndex + 1];
  if (nextLandmark && distance >= nextLandmark.distance) {
    return {
      ...base,
      currentLandmarkIndex: base.currentLandmarkIndex + 1,
      phase: "landmark",
      log: [
        makeLog(day, `You've reached ${nextLandmark.name}!`, "good"),
        ...base.log,
      ].slice(0, 40),
    };
  }

  // trigger a random event? (chance scaled by difficulty)
  if (rng() < cfg.eventChance) {
    const event = pickEvent(base, rng);
    if (event) {
      // The volatility storm event brings a matching stormy sky.
      const eventWeather = event.id === "storm" ? "storm" : base.weather;
      return { ...base, phase: "event", activeEventId: event.id, weather: eventWeather };
    }
  }

  // otherwise, quiet travel
  return {
    ...base,
    log: [
      makeLog(day, quietMessage(rng), "info"),
      ...base.log,
    ].slice(0, 40),
  };
}

const QUIET_MESSAGES = [
  "The trail is calm. Blocks confirm steadily overhead.",
  "You make good progress under a clear sky.",
  "A quiet day. Someone hums a tune about magic internet money.",
  "The wagon rolls on. Fees stay low, spirits stay high.",
  "Nothing but open trail and the hum of the network.",
];

function quietMessage(rng: () => number): string {
  return QUIET_MESSAGES[Math.floor(rng() * QUIET_MESSAGES.length)];
}

function pickEvent(state: GameState, rng: () => number): TrailEvent | null {
  const eligible = TRAIL_EVENTS.filter((e) => !e.canFire || e.canFire(state));
  if (!eligible.length) return null;
  const total = eligible.reduce((sum, e) => sum + e.weight, 0);
  let roll = rng() * total;
  for (const e of eligible) {
    roll -= e.weight;
    if (roll <= 0) return e;
  }
  return eligible[0];
}

export function getEventById(id: string | null): TrailEvent | null {
  if (!id) return null;
  return TRAIL_EVENTS.find((e) => e.id === id) ?? null;
}

export function resolveEventChoice(
  state: GameState,
  choiceIndex: number,
): GameState {
  const event = getEventById(state.activeEventId);
  if (!event) return { ...state, phase: "traveling", activeEventId: null };
  const choice = event.choices[choiceIndex];
  if (!choice) return state;

  const result = choice.resolve(state);
  const rng = makeRng(state.seed + choiceIndex * 104729);

  const inv = clampInventory({
    sats: state.inventory.sats + (result.patch.sats ?? 0),
    food: state.inventory.food + (result.patch.food ?? 0),
    medicine: state.inventory.medicine + (result.patch.medicine ?? 0),
    gear: state.inventory.gear + (result.patch.gear ?? 0),
  });

  let party = state.party;
  if (result.partyDamage) {
    // Scale harmful damage by difficulty; leave healing (negative) untouched.
    const cfg = DIFFICULTIES[state.difficulty];
    const dmg =
      result.partyDamage > 0 ? result.partyDamage * cfg.damageMult : result.partyDamage;
    party = applyPartyDamage(party, dmg, rng);
  }

  const log = [
    makeLog(state.day, result.message, result.tone),
    ...state.log,
  ].slice(0, 40);

  const base: GameState = {
    ...state,
    inventory: inv,
    party,
    log,
    activeEventId: null,
    phase: "traveling",
  };

  if (base.party.every((p) => !p.alive)) {
    return { ...base, phase: "gameover" };
  }
  return base;
}

export function setPace(state: GameState, pace: Pace): GameState {
  return { ...state, pace };
}

export function setRations(state: GameState, rations: Rations): GameState {
  return { ...state, rations };
}

export function leaveLandmark(state: GameState): GameState {
  return { ...state, phase: "traveling" };
}

// Trading at landmarks with traders
export function trade(
  state: GameState,
  item: "food" | "medicine" | "gear",
  qty: number,
  unitPrice: number,
): GameState {
  const cost = qty * unitPrice;
  if (qty > 0 && state.inventory.sats < cost) {
    return {
      ...state,
      log: [makeLog(state.day, "Not enough sats for that purchase.", "bad"), ...state.log].slice(0, 40),
    };
  }
  if (qty < 0 && state.inventory[item] < Math.abs(qty)) {
    return state;
  }
  const inv = clampInventory({
    ...state.inventory,
    sats: state.inventory.sats - cost,
    [item]: state.inventory[item] + qty,
  });
  const verb = qty > 0 ? "Bought" : "Sold";
  return {
    ...state,
    inventory: inv,
    log: [
      makeLog(state.day, `${verb} ${Math.abs(qty)} ${item} for ${Math.abs(cost)} sats.`, "info"),
      ...state.log,
    ].slice(0, 40),
  };
}

// Mini-game: "Stack Sats" — reward applied to state
export function applyMinigameReward(state: GameState, sats: number): GameState {
  const inv = clampInventory({ ...state.inventory, sats: state.inventory.sats + sats });
  return {
    ...state,
    inventory: inv,
    phase: "traveling",
    log: [
      makeLog(state.day, `You stacked ${sats} sats at the claim!`, sats > 0 ? "good" : "info"),
      ...state.log,
    ].slice(0, 40),
  };
}

export function averageHealth(party: Companion[]): number {
  const living = party.filter((p) => p.alive);
  if (!living.length) return 0;
  return Math.round(living.reduce((s, p) => s + p.health, 0) / living.length);
}

export function computeScore(state: GameState): number {
  const living = state.party.filter((p) => p.alive).length;
  const healthBonus = averageHealth(state.party) * 5;
  const satsBonus = state.inventory.sats;
  const survivorBonus = living * 1500;
  const speedBonus = Math.max(0, (200 - state.day) * 25);
  const raw = satsBonus + healthBonus + survivorBonus + speedBonus;
  const scoreMult = DIFFICULTIES[state.difficulty].scoreMult;
  return Math.round(raw * scoreMult);
}
